import { Component, DestroyRef, Input, OnChanges, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { HandleImageErrorDirective } from '../../../directives/handle-image-error.directive';
import { AiChatProductCardModel, AiChatProductCardsService } from '../../../services/ai-chat-product-cards.service';
import { AiChatReplyMentions, isMentioned, pairEntries } from '../../../utils/ai-chat-product-cards';

/**
 * The strip of product cards under a settled Pebbl reply, shared by the widget and /ask-pebbl.
 *
 * Most cards sit beside the entry that names them (see AiProductEntryComponent); the strip holds
 * the ones the reply has no entry for - a comparison laid out as a table, say. A card the reply
 * never mentions at all is left out: on its own, with nothing about it, it reads as a stray
 * picture rather than part of the answer. The cards that are left - products the reply
 * mentions without a product entry for them - sit under a small "Also mentioned" label, so a
 * picture is never shown without saying what it is. When the recommended product is among them
 * the label says "Products in this reply" instead, as the pick is not an aside, and its card
 * carries the same "Recommended" badge an entry would. It is a plain list of links to product
 * pages, scrolling sideways so a 50-product range never widens the bubble.
 * Skeletons hold the final size while SKUs resolve, so the reply does not jump. The SKU itself is
 * never rendered: not as text, alt, title or aria-label.
 */
/** A resolved card, with the frame's `recommended` flag for it. */
interface AiChatStripCard extends AiChatProductCardModel {
  recommended: boolean;
}

@Component({
  selector: 'app-ai-product-cards',
  standalone: true,
  imports: [RouterLink, HandleImageErrorDirective],
  templateUrl: './ai-product-cards.component.html',
  styleUrl: './ai-product-cards.component.scss'
})
export class AiProductCardsComponent implements OnChanges {
  /** Counts strips, so each one's label id is unique on the page. */
  private static instances = 0;

  /* ################### Variable Declaration Start ################### */
  /** SKUs in display order, exactly as the frame sent them. */
  @Input({ required: true }) skus: string[] = [];
  /** Each SKU's `product_link`, index for index with `skus`. */
  @Input() links: (string | null)[] = [];
  /** Each SKU's `recommended` flag, index for index with `skus`. */
  @Input() recommended: boolean[] = [];
  /** Every entry's product link - the cards those entries took are not repeated here. */
  @Input() entryHrefs: (string | null)[] = [];
  /** What the reply names; a card it never mentions is not shown. */
  @Input() mentions: AiChatReplyMentions = { slugs: [], text: '' };

  cards = signal<AiChatStripCard[]>([]);
  isLoading = signal(true);
  /** Names the list for screen readers by its visible label - never by a SKU. */
  readonly labelId = `ai-product-cards-label-${++AiProductCardsComponent.instances}`;

  /** The inputs last resolved - a re-render with the same reply must not fetch again. */
  private resolvedKey: string | null = null;
  private subscription?: Subscription;
  private readonly destroyRef = inject(DestroyRef);
  /* ################### Variable Declaration End ################### */

  constructor(private productCardsService: AiChatProductCardsService) { }

  ngOnChanges(): void {
    const skus = this.skus ?? [];
    const links = this.links ?? [];
    const recommended = this.recommended ?? [];
    const entryHrefs = this.entryHrefs ?? [];
    const mentions = this.mentions;
    const key = JSON.stringify([skus, links, recommended, entryHrefs, mentions]);
    if (key === this.resolvedKey) {
      return;
    }
    this.resolvedKey = key;

    this.subscription?.unsubscribe();
    this.isLoading.set(true);
    this.subscription = this.productCardsService.resolveEach(skus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cards) => {
        const taken = new Set(pairEntries(entryHrefs, cards, links));
        this.cards.set(cards.flatMap((card, index) =>
          card && !taken.has(index) && isMentioned(card, mentions, links[index] ?? null)
            ? [{ ...card, recommended: recommended[index] === true }]
            : []));
        this.isLoading.set(false);
      });
  }

  /** True when the strip holds the recommended product - it is then not labelled an aside. */
  get holdsRecommended(): boolean {
    return this.cards().some((card) => card.recommended);
  }

  /** One skeleton per SKU no entry took - the most the strip can hold once it resolves. */
  get skeletons(): number[] {
    return Array.from({ length: Math.max((this.skus?.length ?? 0) - (this.entryHrefs?.length ?? 0), 0) }, (_, index) => index);
  }
}
